import React, { useMemo, useState, useEffect, useCallback } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table'
import Pagination from '../CommonPage/Pagination.component'
import moment from 'moment'
import './DocumentsTable.style.scss'

export default function DocumentsTable({ data = {}, onDownload, onDelete }) {
  const [sorting, setSorting] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDoc, setPendingDoc] = useState(null)

  const pageSize = 10
  const isLoading = !!data.loading
  const raw = data?.items ?? []

  const formatBytes = (bytes) => {
    if (bytes == null || isNaN(bytes)) return '—'
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return '0 B'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    const val = (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)
    return `${val} ${sizes[i]}`
  }

  const getType = (ct, name) => {
    if (ct) return ct
    const i = name?.lastIndexOf('.')
    return i > -1 ? name.slice(i + 1).toUpperCase() : '—'
  }

  const transformedData = raw.map(item => ({
    filename: item.FileName,
    date: item.LastModified ? moment(item.LastModified).format('DD-MMM-YYYY h:mm A') : '—',
    sortDate: item.LastModified || '',
    type: getType(item.ContentType, item.FileName),
    size: formatBytes(item.SizeBytes),
    sortSize: item.SizeBytes ?? 0,
    source: item.Source || '—',
    full: item,
  }))

  const filteredData = useMemo(() => {
    if (!globalFilter) return transformedData
    const q = globalFilter.toLowerCase()
    return transformedData.filter(row =>
      Object.values(row).some(v => v && v.toString().toLowerCase().includes(q))
    )
  }, [transformedData, globalFilter])

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredData.slice(start, start + pageSize)
  }, [filteredData, currentPage])

  const columns = useMemo(() => [
    { header: 'Filename', accessorKey: 'filename' },
    {
      header: 'Date',
      accessorKey: 'date',
      sortingFn: (a, b) => {
        const av = a.original.sortDate
        const bv = b.original.sortDate
        return av < bv ? -1 : av > bv ? 1 : 0
      }
    },
    { header: 'File type', accessorKey: 'type' },
    {
      header: 'File size',
      accessorKey: 'size',
      sortingFn: (a, b) => a.original.sortSize - b.original.sortSize
    },
    { header: 'Source', accessorKey: 'source' },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const doc = row.original.full
        return (
          <div className="odt-actions">
            {onDownload && (
              <button
                type="button"
                className="odt-btn odt-btn-icon"
                onClick={() => onDownload(doc)}
                aria-label={`Download ${doc.FileName}`}
                title="Download"
              >
                <i className="bi bi-download" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="odt-btn odt-btn-icon odt-danger"
                onClick={() => {
                  setPendingDoc(doc)
                  setConfirmOpen(true)
                }}
                aria-label={`Delete ${doc.FileName}`}
                title="Delete"
              >
                <i className="bi bi-trash" />
              </button>
            )}
          </div>
        )
      },
      enableSorting: false,
    },
  ], [onDownload, onDelete])

  const table = useReactTable({
    data: paginatedData,
    columns,
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
  })

  const totalCount = filteredData.length

  // ---- Confirm Delete modal handlers ----
  const closeConfirm = useCallback(() => {
    setConfirmOpen(false)
    setPendingDoc(null)
  }, [])

  const confirmDelete = useCallback(() => {
    if (pendingDoc && onDelete) onDelete(pendingDoc)
    closeConfirm()
  }, [pendingDoc, onDelete, closeConfirm])

  // Close on ESC
  useEffect(() => {
    if (!confirmOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') closeConfirm()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [confirmOpen, closeConfirm])

  return (
    <div className="other-docs-table">
      <div className="odt-controls">
        <input
          className="odt-search"
          type="text"
          placeholder="Search documents..."
          value={globalFilter}
          onChange={(e) => {
            setCurrentPage(1)
            setGlobalFilter(e.target.value)
          }}
        />
      </div>

      <div className="odt-table-scroll">
        <table className="odt-table">
          <thead>
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(h => {
                  const isSorted = h.column.getIsSorted()
                  return (
                    <th key={h.id} onClick={h.column.getToggleSortingHandler()}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      <i
                        className={`bi ${
                          isSorted === 'asc'
                            ? 'bi-sort-down'
                            : isSorted === 'desc'
                            ? 'bi-sort-up'
                            : ''
                        } odt-sort-icon`}
                      />
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={columns.length} className="odt-loading">
                  Loading documents…
                </td>
              </tr>
            )}

            {!isLoading && (table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="odt-empty">
                  <div className="odt-empty-box">
                    <i className="bi bi-folder2-open" />
                    <h4>No documents found</h4>
                    <p>Your search didn’t match any results.</p>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>

      <div className="odt-pagination">
        <Pagination
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Confirm Delete Modal */}
      {confirmOpen && (
        <div
          className="odt-modal-backdrop"
          role="presentation"
          onClick={closeConfirm}
        >
          <div
            className="odt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="odt-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="odt-modal-header">
              <h3 id="odt-confirm-title">Delete document?</h3>
              <button
                type="button"
                className="odt-btn odt-btn-icon"
                aria-label="Close"
                onClick={closeConfirm}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="odt-modal-body">
              <p>
                Are you sure you want to delete{' '}
                <strong>{pendingDoc?.FileName}</strong>?
              </p>
            </div>
            <div className="odt-modal-footer">
              <button
                type="button"
                className="odt-btn"
                onClick={closeConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="odt-btn odt-danger"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}